import React from "react";
import { motion } from "framer-motion";
import { FileText, Brain, Download } from "lucide-react";
import { useNavigate } from "react-router-dom";

const TrustedBySection = () => {
  const navigate = useNavigate();
  const waitlistUrl = "https://portal.selfsync.ai/sign-up";

  const reportFeatures = [
    {
      icon: <Brain className="w-12 h-12 text-purple-600" />,
      title: "AI Psychology Report",
      description: "Get a 200-word personality summary that reveals exactly how this person thinks, works, and fits your team.",
      highlight: "Instant insights"
    },
    {
      icon: <FileText className="w-12 h-12 text-cyan-500" />,
      title: "15 Critical Traits",
      description: "See scores on leadership, teamwork, adaptability, stress tolerance, and 11 other traits that predict success.",
      highlight: "99% accuracy"
    },
    {
      icon: <Download className="w-12 h-12 text-green-500" />,
      title: "Ready-to-Share PDF",
      description: "Professional reports delivered instantly to your inbox. Share with your team or keep for hiring records.",
      highlight: "Delivered instantly"
    }
  ];

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className="py-12 px-6 md:px-14 lg:px-12 bg-gradient-to-br from-gray-50 to-white"
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: 0.2 } },
      }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            What You{" "}
            <span className="bg-gradient-to-r from-purple-600 to-cyan-500 text-transparent bg-clip-text">
              Actually Get
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Actionable personality insights that transform how you hire and manage people
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
  {reportFeatures.map((feature, index) => (
    <motion.div
      key={index}
      className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 text-center group border border-gray-100 flex flex-col h-full" // 🔧 Make card flex with full height
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.6 }}
    >
      <div className="mb-6 flex justify-center">
        <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
          {feature.icon}
        </div>
      </div>

      <h3 className="text-2xl font-bold text-gray-900 mb-4">
        {feature.title}
      </h3>

      <p className="text-gray-600 leading-relaxed mb-6">
        {feature.description}
      </p>

      {/* Highlight Badge pushed to bottom */}
      <div className="mt-auto inline-block bg-gradient-to-r from-purple-100 to-cyan-100 text-purple-700 font-bold px-4 py-2 rounded-full text-sm">
        {feature.highlight}
      </div>
    </motion.div>
  ))}
</div>


        {/* CTA Section */}
        <motion.div
          className="text-center bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            See What a Real Report Looks Like
          </h3>
          <p className="text-gray-600 mb-8">
            View our sample report to understand the depth of insights you'll receive
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              className="custom-button text-white font-bold px-5 md:px-6 py-2.5 rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all shadow-lg text-base md:text-lg"
              onClick={() => window.open(waitlistUrl, "_blank")}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Start Your Free Assessment
            </motion.button>
            
            <motion.button
              className="border-2 border-purple-600 text-purple-600 font-bold px-6 py-2 rounded-lg hover:bg-purple-50 transition-all text-lg"
              onClick={() => navigate("/sample-report")}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              View Sample Report
            </motion.button>
          </div>
          
          <p className="text-sm text-gray-500 mt-4">
            No credit card required • Get results in 30 minutes • See exactly what your candidates see
          </p>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default TrustedBySection;
